// =============================================================================
// GAP ANSWER MODE — type · dictate · talk it through
//
// The owner's spec, verbatim (walk finding W22, 2026-07-24): "the address this
// needs to be a two-way voice interview interaction as an option". On the
// MISSING EVIDENCE gap sheet Meira asks a real interview question, but the only
// answer paths were TYPE or DICTATE-into-the-textarea. This module is the pure
// logic behind the third path: he talks, she answers back, and what he SAID
// lands in the same place a typed answer lands.
//
// AN OPTION, NEVER A DEFAULT: `type` is the initial mode. Nothing here starts a
// microphone — the surface only ever starts a session from an explicit tap.
//
// ── THE NO-INVENTION BOUNDARY (the foundational law, enforced in code) ───────
// A live two-way loop has TWO speakers, so the committed artifact must be
// separated from the conversation that produced it:
//
//   * `spokenUserAnswer` builds the capture from `role === 'user'` turns ONLY.
//     Meira's questions, her follow-ups, and any summary she offers are
//     DISPLAY-ONLY — they are never part of what gets sent.
//   * `guardSpokenCommit` is the second wall: it re-checks the assembled
//     capture on the way out and REJECTS it if a substantial span of Meira's
//     own words appears in it without also appearing in his. An agent-authored
//     string therefore cannot reach the commit payload even if a future edit to
//     the surface wired the wrong array into it.
//   * No user speech at all is an honest non-outcome (`no_user_speech`) — the
//     gap stays open. The system never fills the silence on his behalf.
//
// This mirrors the server's own posture: apps/api/routes/jobs.routes.ts's
// gap-address/commit already extracts from `transcript.filter(t => t.role ===
// 'user')` and runs the result through gateGapCommit (apps/api/services/
// grounding/GapCommitGate.ts), which fail-closes unless every specific traces
// back to the user's capture. This module is the same law held on the client,
// so an agent line is never even offered for sending.
// =============================================================================

/** The three answer paths offered on the gap sheet. */
export type GapAnswerMode = 'type' | 'dictate' | 'talk';

/** The default. Voice is opt-in, always — never an auto-starting mic. */
export const DEFAULT_GAP_ANSWER_MODE: GapAnswerMode = 'type';

export const GAP_ANSWER_MODES: readonly GapAnswerMode[] = ['type', 'dictate', 'talk'] as const;

/** The label + one honest line of what each mode actually does. */
export function describeGapAnswerMode(mode: GapAnswerMode): { label: string; hint: string } {
  switch (mode) {
    case 'type':
      return { label: 'Type', hint: 'Write your answer and send it.' };
    case 'dictate':
      return {
        label: 'Dictate',
        hint: 'Speak into the box. Read it back before you send — you can fix anything the mic got wrong.',
      };
    case 'talk':
      return {
        label: 'Talk it through',
        hint: "Meira asks out loud, you answer out loud. Only what you say gets saved.",
      };
  }
}

// ── Availability + honest degrade ───────────────────────────────────────────

/** Why the live loop cannot run here. Never a dead button: the option stays
 *  visible with the real reason, and type/dictate remain untouched. */
export type TalkUnavailableReason = 'no_mic_api' | 'insecure_context' | 'not_entitled' | 'connect_failed';

export type TalkAvailability =
  | { available: true }
  | { available: false; reason: TalkUnavailableReason; explanation: string };

/** Resolve from the real browser environment. Pure: the caller reads the
 *  globals and passes them in, so this is testable under node. */
export function resolveTalkAvailability(env: {
  hasMediaDevices: boolean;
  isSecureContext: boolean;
}): TalkAvailability {
  if (!env.isSecureContext) {
    return {
      available: false,
      reason: 'insecure_context',
      explanation:
        "Your browser only allows the microphone on a secure (https) connection, so the live conversation can't open here. Typing or dictating still works.",
    };
  }
  if (!env.hasMediaDevices) {
    return {
      available: false,
      reason: 'no_mic_api',
      explanation:
        "This browser doesn't support the live voice conversation. Typing or dictating still works — you'll get the same result either way.",
    };
  }
  return { available: true };
}

/** Map a raw transport/mint failure to something honest and human. Unknown
 *  messages pass through rather than being smoothed into a lie. */
export function describeTalkError(message: string): string {
  const m = (message || '').toLowerCase();
  if (m.includes('entitle') || m.includes('403') || m.includes('forbidden')) {
    return "The live voice conversation isn't part of your plan yet — typing or dictating works the same way here.";
  }
  if (m.includes('permission') || m.includes('notallowed') || m.includes('denied')) {
    return "I couldn't get to the microphone — your browser blocked it. Check the mic permission for this site, or type your answer instead.";
  }
  if (m.includes('not_found') || m.includes('404')) {
    return "I couldn't open a conversation about this gap just now. Typing or dictating still works.";
  }
  return message?.trim()
    ? `The live conversation stopped: ${message.trim()}`
    : "The live conversation stopped unexpectedly. Typing or dictating still works.";
}

// ── The live-loop state machine ─────────────────────────────────────────────

/**
 * Every state the "talk it through" surface can be in. These are exactly the
 * states the style-guide entry renders (hard rule #5).
 *   idle                 — offered, nothing started, mic untouched
 *   connecting            — session minting / WebRTC handshake
 *   listening             — live, waiting on him
 *   agent_speaking        — live, Meira is talking
 *   ready_to_confirm      — he ended it and there is something he said to review
 *   error                 — the honest failure surface
 *   unsupported           — offered, explained, never a dead button
 */
export type VoiceLoopState =
  | 'idle'
  | 'connecting'
  | 'listening'
  | 'agent_speaking'
  | 'ready_to_confirm'
  | 'error'
  | 'unsupported';

export const VOICE_LOOP_STATES: readonly VoiceLoopState[] = [
  'idle',
  'connecting',
  'listening',
  'agent_speaking',
  'ready_to_confirm',
  'error',
  'unsupported',
] as const;

export interface VoiceLoopInput {
  /** False when resolveTalkAvailability said no. */
  supported: boolean;
  /** Any surfaced error; wins over every live state. */
  error: string | null;
  /** The user ended the session (tapped done / hung up). */
  ended: boolean;
  /** At least one non-empty USER turn was captured. */
  hasUserSpeech: boolean;
  /** The SDK's lifecycle status, straight from useLivingConversation. */
  status: 'disconnected' | 'connecting' | 'connected' | 'disconnecting';
  /** The SDK's isSpeaking — true while Meira's audio is playing. */
  isSpeaking: boolean;
}

/**
 * Derive the single state the surface should render. Precedence is deliberate:
 * an unsupported browser and a real error both outrank any optimistic live
 * state, and "ended with nothing said" falls back to `idle` rather than
 * offering a confirm step over an empty capture (the honest non-outcome).
 */
export function deriveVoiceLoopState(input: VoiceLoopInput): VoiceLoopState {
  if (!input.supported) return 'unsupported';
  if (input.error) return 'error';
  if (input.ended) return input.hasUserSpeech ? 'ready_to_confirm' : 'idle';
  if (input.status === 'connecting' || input.status === 'disconnecting') return 'connecting';
  if (input.status === 'connected') return input.isSpeaking ? 'agent_speaking' : 'listening';
  return input.hasUserSpeech ? 'ready_to_confirm' : 'idle';
}

/** Is a live session currently up (for the stop/hang-up affordance)? */
export function isLive(state: VoiceLoopState): boolean {
  return state === 'connecting' || state === 'listening' || state === 'agent_speaking';
}

/** One honest status line per state. */
export function describeVoiceLoopState(state: VoiceLoopState): string {
  switch (state) {
    case 'idle':
      return "Meira will ask you about this gap out loud. Nothing records until you start.";
    case 'connecting':
      return 'Connecting…';
    case 'listening':
      return "Listening — take your time.";
    case 'agent_speaking':
      return 'Meira is talking…';
    case 'ready_to_confirm':
      return "Here's what you said. Edit anything that came out wrong, then send it.";
    case 'error':
      return 'The live conversation stopped.';
    case 'unsupported':
      return "The live conversation can't run in this browser.";
  }
}

// ── Turn assembly + the no-invention commit guard ───────────────────────────

/** One line of the live conversation. `agent` lines are DISPLAY ONLY. */
export interface SpokenTurn {
  role: 'agent' | 'user';
  text: string;
}

/** Comparison form: case-folded, apostrophes DROPPED (they sit inside words —
 *  "what's" must fold to the same shape as "whats", or a re-punctuated agent
 *  line would slip past the guard below), every other run of punctuation
 *  collapsed to a single space. */
function normalizeSpeech(s: string): string {
  return s
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * A span of Meira's speech this long (normalized) counts as HER words rather
 * than an incidental overlap. Short spans ("yeah", "the team", "okay") legitimately
 * appear in both halves of a conversation and must not trip the guard.
 */
export const AGENT_ECHO_MIN_CHARS = 24;

/**
 * Build the capture from the USER's turns only. Meira's questions and any
 * summary she offers are excluded here — the single place the capture is
 * assembled, so there is no second path that could include them.
 */
export function spokenUserAnswer(turns: readonly SpokenTurn[]): string {
  return turns
    .filter((t) => t.role === 'user')
    .map((t) => (t.text ?? '').trim())
    .filter(Boolean)
    .join(' ')
    .trim();
}

export type SpokenCommitOutcome =
  | { ok: true; capture: string }
  | { ok: false; reason: 'no_user_speech' | 'agent_authored'; message: string };

/**
 * THE GUARD. Called before the spoken answer is allowed anywhere near the
 * gap-address send path, and again at send time.
 *
 * Rejects when:
 *   no_user_speech  — nothing he said (empty capture, or no user turns at all).
 *                     An honest non-outcome; the gap stays open. The system
 *                     never writes something he did not say.
 *   agent_authored  — a substantial span of Meira's own words is present in the
 *                     capture and absent from his speech. That is her sentence,
 *                     not his, and it cannot be committed as his evidence.
 *
 * His own EDITS are explicitly allowed: the guard checks for agent authorship,
 * it does not freeze his words. He can rewrite the transcript freely — what he
 * cannot do (and what a surface bug cannot do on his behalf) is send hers.
 */
export function guardSpokenCommit(
  capture: string,
  turns: readonly SpokenTurn[],
): SpokenCommitOutcome {
  const trimmed = (capture ?? '').trim();
  const userSpeech = spokenUserAnswer(turns);

  if (!trimmed || !userSpeech) {
    return {
      ok: false,
      reason: 'no_user_speech',
      message: "I didn't catch anything you said — this one stays open for now.",
    };
  }

  const captureHay = normalizeSpeech(trimmed);
  const userHay = normalizeSpeech(userSpeech);

  for (const turn of turns) {
    if (turn.role !== 'agent') continue;
    const agentSpan = normalizeSpeech(turn.text ?? '');
    if (agentSpan.length < AGENT_ECHO_MIN_CHARS) continue;
    if (captureHay.includes(agentSpan) && !userHay.includes(agentSpan)) {
      return {
        ok: false,
        reason: 'agent_authored',
        message:
          "That's Meira's wording, not yours — only what you said can be saved to your corpus. Say it in your own words and I'll keep that.",
      };
    }
  }

  return { ok: true, capture: trimmed };
}
